import './style.ts';
import { Suspense, useMemo, forwardRef, useImperativeHandle, lazy } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryProvider } from '@unifyapps/network/utils/QueryProvider';
import { getStyleItemConfig } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles/styles/utils';
import { modalAppearanceUISchema } from '@unifyapps/defs/blocks/Modal/appearanceSchema';
import ThemeRegistry from './ThemeProvider';
import { SnackbarProvider } from '@unifyapps/ui/components/Snackbar';
import type { MatrixWebComponent } from './matrixWebComponent';
import './matrixWebComponent';
import AuthProvider from './AuthProvider';
import { INTERFACE_THEME, UA_THEME_VARS } from '@unifyapps/style/themes/consts';
import { MatrixModuleDefsProvider } from '@unifyapps/carbon/providers/moduleDefs/ModuleDefsProvider';
import { getIdWithPrefix } from '@unifyapps/carbon/utils/id';
import { MqttProvider } from '@unifyapps/network/mqttClient';
import { ApplicationType } from '@unifyapps/defs/types/interface';
import ContainerElementContext from '@unifyapps/ui/providers/ContainerElementContext';
import { QueryMeta } from '@unifyapps/network/utils/fetch/types';
import { useFindDeployedEntityById } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import { supportedRouters } from './constants';
import { DocElementsContext } from '@unifyapps/ui/providers/DocElementsContext';
import {
  DOC_ELEMENTS_DEFAULT_VALUE,
  DocElementsContextType,
} from '@unifyapps/ui/providers/DocElementsContext/DocElementsContext';
import LocaleProvider from '@unifyapps/carbon/context/LocaleProvider';
import { EventNames, type DMCustomEvent, type UAWebApplicationRef } from './types';
import { emitCustomEvents } from './helpers/emitCustomEvents';
import { DM_ROOT_CLASS } from '@unifyapps/carbon/delta-matrix/constants';
import type { EmitEventType } from '@unifyapps/defs/hooks/useBlockEvents';
import { ClientContextProvider } from '@unifyapps/carbon/context/client/ClientContext';
import type { ClientContextValue } from '@unifyapps/carbon/context/client/types';
import { ClientType } from '@unifyapps/carbon/context/client/types';
import { App } from './BaseApp';
import { setupContainerEl } from './helpers/setupContainerEl';

const CopilotChatApp = lazy(
  () => import(/* webpackChunkName: "copilot-chat" */ './CopilotChatApp'),
);

// Q. why we need container?
// A. to anchor different UI elements to it
// Q. UI package provider a context that accepts a container
// A. whenever a popover like element is mounted it will use that context to pass a container for anchor
// 1. In delta-matrix, we set that context to be the container element provided by the host app
// 2. In runner, we don't set the context to anything so it will use the body as default
// 3. In preview and builder of matrix app, we will wrap the layout of necessary canvas with the context of ui package and render a div beside it with the appropriate
// classname and data-color-scheme
// 1. modal, drawer, are controlled i.e we can set their container el
// 2. for each snackbar we will set it's container
// we expect the container to have property classname and the data-color-scheme

const noop = (s: unknown) => s;
const stylesToImportForTailwindParser = getStyleItemConfig(noop as any, ApplicationType.WEB);
// so the unused variable is not removed by the compiler
Boolean(stylesToImportForTailwindParser);
Boolean(modalAppearanceUISchema);

type Props = {
  interfaceId: string; // Interface id to be rendered
  pageId: string; // Ppage id/slug to be rendered
  pageInputs?: Record<string, unknown>; // Page inputs to be passed to the page
  apiDomain: string; // Domain where api requests are made
  tenantDomain: string; // Domain to be passed in x-ua-domain header, this is optional. Used in integrations like shopify
  routerType?: keyof typeof supportedRouters;
  basePath?: string; // React router's base path
  renderInShadowDom?: boolean; // Whether to render the app in shadow dom
  isPublicInterface?: boolean; // Whether the interface is public or not
  clientContextApp?: ClientContextValue['app'];
  customEvent?: DMCustomEvent;
  onPageEvent?: EmitEventType;
};

function Application({
  routerType = 'memory',
  pageId: _pageId,
  pageInputs,
  basePath,
  interfaceId,
  queryMeta,
  onPageEvent,
}: Pick<
  Props,
  'routerType' | 'pageId' | 'pageInputs' | 'basePath' | 'interfaceId' | 'onPageEvent'
> & {
  queryMeta: QueryMeta;
}) {
  const { data: interfaceRecord, isLoading } = useFindDeployedEntityById<InterfaceEntity>(
    UAEntityTypes.Interface,
    interfaceId,
    {
      query: {
        retry: 3,
      },
    },
  );

  if (isLoading || !interfaceRecord) {
    return null;
  }

  const pageId = _pageId || interfaceRecord.properties.navigation.defaultPageId;

  if (interfaceRecord.properties.manifest?.type === ApplicationType.COPILOT) {
    return (
      <CopilotChatApp
        interfaceRecord={interfaceRecord}
        pageId={pageId}
        pageInputs={pageInputs}
        routerType={routerType}
        basePath={basePath}
        interfaceId={interfaceId}
        queryMeta={queryMeta}
      />
    );
  }

  return (
    <App
      interfaceId={interfaceId}
      queryMeta={queryMeta}
      routerType={routerType}
      pageId={pageId}
      pageInputs={pageInputs}
      basePath={basePath}
      onPageEvent={onPageEvent}
    />
  );
}

export const UAWebApplication = forwardRef<
  UAWebApplicationRef,
  Props & { containerEl: HTMLElement; docElementsContextValue: DocElementsContextType | null }
>(function UAWebApplication(
  {
    containerEl,
    interfaceId,
    pageId,
    apiDomain,
    tenantDomain,
    pageInputs,
    routerType = 'memory',
    basePath,
    renderInShadowDom,
    isPublicInterface,
    docElementsContextValue,
    clientContextApp,
    onPageEvent,
  },
  ref,
) {
  // (window as any).__unifyBaseUrl = apiDomain;
  (window as any).__unifyAppId = interfaceId;
  (window as any).__unifyTenantDomain = tenantDomain;

  // prefixing our tailwind config with delta-matrix to avoid conflicts with the host app
  containerEl.classList.add(DM_ROOT_CLASS);
  // without ua-variables, interface-theme class, the variables are not applied on the popover
  containerEl.classList.add(UA_THEME_VARS, INTERFACE_THEME);

  // Set the app id on the container element for a unique app selector
  containerEl.dataset.appId = interfaceId;

  // Set the page id on the container element for a unique page selector
  containerEl.dataset.pageId = pageId;

  containerEl.dir = 'ltr';

  /**
   * The contain: layout is being used to create a containing block for fixed position elements.
   * For example: Drawer when opened from our app which is embedded in salesforce is a fixed positioned element.
   * Now drawer will open relative to viewport if there is no containing block defined by either the contain property
   * or other properties as defined in https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block.
   *
   * Other solution would be to position the drawer with absolute position but this approach will lead to issues of z-index as no new stacking context will be created.
   * And hence elements like drawer will still interfere with the elements of the host app.
   *
   * References:
   * https://developer.mozilla.org/en-US/docs/Web/CSS/contain
   */
  containerEl.style.contain = 'layout';

  const queryMeta = useMemo<QueryMeta>(
    () => ({ interfaceId, apiDomain, isPublicInterface }),
    [interfaceId, apiDomain],
  );

  useImperativeHandle(ref, () => ({
    goToPage: (args) => {
      emitCustomEvents({ event: EventNames.GoToPage, props: args });
    },
  }));

  return (
    <Suspense>
      {/*todo: pass actual app here when it's known*/}
      <ClientContextProvider
        {...{ client: ClientType.DeltaMatrix, app: clientContextApp ?? 'UNKNOWN' }}
      >
        <DocElementsContext.Provider value={docElementsContextValue ?? DOC_ELEMENTS_DEFAULT_VALUE}>
          <ContainerElementContext.Provider value={containerEl as HTMLDivElement}>
            <ThemeRegistry renderInShadowDom={renderInShadowDom}>
              <QueryProvider queryMeta={queryMeta}>
                <SnackbarProvider>
                  <AuthProvider>
                    <LocaleProvider>
                      <MatrixModuleDefsProvider>
                        <MqttProvider resourceType="PLATFORM">
                          <Application
                            interfaceId={interfaceId}
                            pageId={pageId}
                            pageInputs={pageInputs}
                            routerType={routerType}
                            basePath={basePath}
                            queryMeta={queryMeta}
                            onPageEvent={onPageEvent}
                          />
                        </MqttProvider>
                      </MatrixModuleDefsProvider>
                    </LocaleProvider>
                  </AuthProvider>
                </SnackbarProvider>
              </QueryProvider>
            </ThemeRegistry>
          </ContainerElementContext.Provider>
        </DocElementsContext.Provider>
      </ClientContextProvider>
    </Suspense>
  );
});

const containerIdVsRoot = new Map<string, Root>();
const containerIdVsMatrixWebComponent = new Map<string, MatrixWebComponent>();
const containerIdVsContainerEl = new Map<string, HTMLElement>();

export function mount(externalContainerEl: HTMLElement, props: Props) {
  const { renderInShadowDom, customEvent } = props ?? {};

  const containerId = setupContainerEl({
    externalContainerEl,
    renderInShadowDom,
    containerIdVsContainerEl,
    containerIdVsMatrixWebComponent,
  });

  const containerEl = containerIdVsContainerEl.get(containerId);
  if (!containerEl) {
    // This should never happen, because for every containerId we set the containerEl very first time only when mount is called
    // Throwing error here for type safety
    throw new Error('UnifyApps: containerEl not found');
  }

  const matrixWebComponent = containerIdVsMatrixWebComponent.get(containerId);

  // If root is not present for given container, create new root.
  // If root is already present, use it as we don't want to unmount the app when the mount function is called again for same container
  let root = containerIdVsRoot.get(containerId);
  if (!root) {
    root = createRoot(containerEl);
    containerIdVsRoot.set(containerId, root);
  }

  if (customEvent) {
    emitCustomEvents(customEvent);
  }

  root.render(
    <UAWebApplication
      {...props}
      containerEl={containerEl}
      docElementsContextValue={
        matrixWebComponent
          ? {
              headEl: matrixWebComponent.getShadowHead(),
              bodyEl: matrixWebComponent.shadowRoot as ShadowRoot,
            }
          : null
      }
    />,
  );
  containerIdVsRoot.set(containerId, root);
}

// We can use uaMount function to render delta matrix app in any web app
(window as any).uaMount = mount;

// UAWebApplication component can be used to render delta matrix app in host apps which are already using react.
// Internally uaMount also renders UAWebApplication component
// We use this component in react-sdk
(window as any).UAWebApplication = UAWebApplication;
